import { useEffect, useState } from "react";
import cn from "classnames";
import Combox from "~/components/shared/Combox";
import { useComboxContext } from "~/components/shared/Combox/ComboxContext";
import ComboxTrigger from "~/components/shared/Combox/ComboxTrigger";
import ComboxPopover from "~/components/shared/Combox/ComboxPopover";
import ComboxFilterInput from "~/components/shared/Combox/ComboxFilterInput";
import ComboxList from "~/components/shared/Combox/ComboxList";
import ComboxItem from "~/components/shared/Combox/ComboxItem";
import Icon from "~/components/shared/Icon";
import Emojify from "~/components/shared/Emojify";

interface OrgData {
  uuid: string;
  name: string;
  iconUrl: string;
  membersCount: number;
  ssoRequired: boolean;
  ssoLoginUrl: string | null;
}

interface Props {
  organizations: OrgData[];
  selectedUuid?: string;
  name: string;
  labelId?: string;
}

const TRIGGER_ID = "oauth-org-picker-trigger";
const ORG_CHANGED_EVENT = "oauth:org-changed";

const notifyOrgChanged = (org?: OrgData) => {
  window.dispatchEvent(
    new CustomEvent(ORG_CHANGED_EVENT, {
      detail: {
        ssoRequired: org?.ssoRequired ?? false,
        ssoLoginUrl: org?.ssoLoginUrl ?? null,
      },
    }),
  );
};

const memberLabel = (count: number) => `${count} ${count === 1 ? "member" : "members"}`;

const OrgAvatar = ({ src, size }: { src: string; size: number }) => (
  <img
    src={src}
    alt=""
    width={size}
    height={size}
    className="shrink-0 rounded object-cover"
    style={{ width: size, height: size }}
  />
);

const SsoBadge = () => (
  <code className="text-charcoal-600 ml-2 inline-block shrink-0 rounded-sm bg-gray-200 px-1.5 py-0.5 text-xs font-normal">
    SSO Required
  </code>
);

const HiddenInput = ({ name }: { name: string }) => {
  const { selectedValue } = useComboxContext();
  return <input type="hidden" name={name} value={selectedValue || ""} readOnly={true} />;
};

const TriggerContent = ({ organizations }: { organizations: OrgData[] }) => {
  const { selectedValue, open } = useComboxContext();
  const org = organizations.find((candidate) => candidate.uuid === selectedValue);

  return (
    <span className="flex w-full items-center gap-2">
      {org ? (
        <>
          <OrgAvatar src={org.iconUrl} size={28} />
          <span className="flex min-w-0 flex-col items-start">
            <span className="flex items-center truncate">
              <Emojify text={org.name} />
              {org.ssoRequired && <SsoBadge />}
            </span>
            <span className="text-charcoal-500 text-xs font-semibold">
              {memberLabel(org.membersCount)}
            </span>
          </span>
        </>
      ) : (
        <span className="text-charcoal-300">Select an organization</span>
      )}
      <Icon
        icon="custom/outline/chevron-down"
        className={cn(
          "text-charcoal-400 ml-auto h-4 w-4 shrink-0 self-center transition-transform",
          open && "rotate-180",
        )}
      />
    </span>
  );
};

const AuthorizeOrganizationPicker = ({ organizations, selectedUuid, name, labelId }: Props) => {
  const [open, setOpen] = useState(false);
  // Name the trigger by the field label plus its own selected-org text, so a
  // screen reader announces e.g. "Select organization: Test, 11 members".
  const triggerLabelledBy = [labelId, TRIGGER_ID].filter(Boolean).join(" ");

  useEffect(() => {
    notifyOrgChanged(organizations.find((org) => org.uuid === selectedUuid));
  }, [organizations, selectedUuid]);

  const handleValueChange = (uuid: string) =>
    notifyOrgChanged(organizations.find((org) => org.uuid === uuid));

  return (
    <Combox
      open={open}
      onOpenChange={setOpen}
      defaultValue={selectedUuid}
      onValueChange={handleValueChange}
    >
      <ComboxTrigger
        id={TRIGGER_ID}
        aria-labelledby={triggerLabelledBy}
        subtle={true}
        disclosure={false}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-left hover:border-gray-400 data-[state=open]:border-lime-500"
      >
        <TriggerContent organizations={organizations} />
      </ComboxTrigger>
      <ComboxPopover className="ph-no-capture">
        <ComboxFilterInput placeholder="Search organizations" />
        <ComboxList emptyState="No organizations found">
          {organizations.map((org) => (
            <ComboxItem key={org.uuid} value={org.uuid}>
              <OrgAvatar src={org.iconUrl} size={28} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">
                  <Emojify text={org.name} />
                </span>
                <span className="text-charcoal-500 text-xs">{memberLabel(org.membersCount)}</span>
              </span>
              {org.ssoRequired && <SsoBadge />}
            </ComboxItem>
          ))}
        </ComboxList>
      </ComboxPopover>
      <HiddenInput name={name} />
    </Combox>
  );
};

export default AuthorizeOrganizationPicker;
